import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FIELDS, FIELDS_DEFAULT } from "../../../../constants/PostFormFields";
import get from "lodash/get";
import { PostFieldLabel } from "../PostFieldLabel/PostFieldLabel";
import { AccountPlatformType } from "../../../../shared/types/accounts";
import { TextInput } from "../../../../ui/ecosystems/forms/inputs";
import { useDebouncedCallback } from "use-debounce";
import { MediaUrlInfo } from "../../../../shared/types/post";
import { getMediaType } from "../postFormValidator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { TiktokPrivacyLevel } from "../../../../features/post/types";
import { Icon } from "@dialogueconsulting/sked-ui";
import PostFieldError from "../PostFieldError/PostFieldError";
import { extractMediaUrls } from "../utils";
import type { FormValidationErrors } from "../../../../shared/types/post";
import type { PlatformSpec } from "../../../../constants/Platforms";
import { useRegisterFlush } from "../platform-fields/flush-registry";
import { SwitchFieldRow } from "../components/SwitchFieldRow/SwitchFieldRow";

interface FieldInfo {
  label: string;
  field: string;
  info?: { label: string; content: string };
}

const allowCommentField = {
  label: "Allow comments",
  field: FIELDS.TT_ALLOW_COMMENTS,
} as const;

const videoPostAllowFields = [
  allowCommentField,
  {
    label: "Allow stitch",
    field: FIELDS.TT_ALLOW_STITCH,
  },
  {
    label: "Allow duet",
    field: FIELDS.TT_ALLOW_DUET,
  },
] as const;
const autoAddMusicField = {
  label: "Auto add music",
  field: FIELDS.TT_AUTO_ADD_MUSIC,
  info: {
    label: "About auto add music",
    content:
      "Automatically add recommended music to the post, you can change the music later in the TikTok app.",
  },
};
const postPrivacyConfig: { value: TiktokPrivacyLevel; label: string }[] = [
  {
    value: "PUBLIC_TO_EVERYONE",
    label: "Visible to all TikTok users",
  },
  {
    value: "MUTUAL_FOLLOW_FRIENDS",
    label: "Only visible to mutual followers",
  },
  {
    value: "FOLLOWER_OF_CREATOR",
    label: "Only visible to followers of the account",
  },
  {
    value: "SELF_ONLY",
    label: "Private, only visible to the account itself",
  },
];
export const TiktokSwitchAllows = ({
  postFormValue,
  updateField,
  allDisabled,
  errors,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  postFormValue: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateField: (field: string, value: any) => void;
  allDisabled: boolean;
  platforms?: PlatformSpec[];
  selectedPlatform?: AccountPlatformType | "LINKED";
  errors?: FormValidationErrors;
}) => {
  const [titleValue, setValue] = useState("");
  const containerRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (get(postFormValue, FIELDS.TT_TITLE)) {
      setValue(get(postFormValue, FIELDS.TT_TITLE));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bounce = useDebouncedCallback((value) => {
    const title = get(postFormValue, FIELDS.TT_TITLE);
    if (value != title) {
      updateField(FIELDS.TT_TITLE, value);
    }
  }, 200);
  // Registered before the early return below so the hook runs unconditionally.
  useRegisterFlush(bounce.flush);

  if (!postFormValue) {
    return null;
  }

  const media = extractMediaUrls(postFormValue);
  const photoMedia = media.filter((item) => getMediaType(item) === "image");
  const isVideo =
    !!media.length && media.every((item) => getMediaType(item) === "video");
  const isPhoto =
    !!media.length && media.every((item) => getMediaType(item) === "image");

  const onSwitchChange = (field: string) => {
    const prevValue = get(postFormValue, field, true);
    updateField(field, !prevValue);
  };
  const renderSwitchField = (item: FieldInfo) => (
    <SwitchFieldRow
      key={item.field}
      data-testid={`tiktok-switch-${item.field.split(".").pop()}`}
      className="mb-2.5"
      label={item.label}
      info={item.info}
      checked={Boolean(get(postFormValue, item.field, true))}
      onCheckedChange={() => onSwitchChange(item.field)}
      disabled={allDisabled}
    />
  );

  return (
    <TiktokSwitchAllowsWrapper ref={containerRef}>
      {isVideo && videoPostAllowFields.map(renderSwitchField)}
      {isPhoto && (
        <>
          {photoMedia.length > 1 && (
            <>
              <LabelStyled>Cover</LabelStyled>
              <div style={{ marginBottom: 10 }}>
                <Select
                  value={Math.min(
                    get(postFormValue, FIELDS.TT_PHOTO_COVER_INDEX) ||
                      FIELDS_DEFAULT.TT_PHOTO_COVER_INDEX,
                    photoMedia.length - 1
                  ).toString()}
                  onValueChange={(value) => {
                    updateField(FIELDS.TT_PHOTO_COVER_INDEX, parseInt(value));
                  }}
                  disabled={allDisabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Photo Cover Index">
                      {renderCoverIndex(photoMedia)({
                        value: Math.min(
                          get(postFormValue, FIELDS.TT_PHOTO_COVER_INDEX) ||
                            FIELDS_DEFAULT.TT_PHOTO_COVER_INDEX,
                          photoMedia.length - 1
                        ),
                      })}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {photoMedia.map((_, index) => (
                      <SelectItem key={index} value={index.toString()}>
                        {renderCoverIndex(photoMedia)({ value: index })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <LabelStyled>Title</LabelStyled>
          <div className="mb-2.5 flex items-center">
            <TextInput
              data-testid="title-input"
              value={titleValue}
              disabled={allDisabled}
              placeholder="The title of your photos"
              onChange={(value: string) => {
                setValue(value);
                bounce(value);
              }}
            />
          </div>
          <PostFieldError
            className="mt-4"
            fields={[FIELDS.TT_TITLE]}
            errors={errors}
          />
          <LabelStyled>Privacy level</LabelStyled>
          <div style={{ marginBottom: 10 }}>
            <Select
              value={
                get(postFormValue, FIELDS.TT_PRIVACY_LEVEL) ||
                FIELDS_DEFAULT.TT_PRIVACY_LEVEL
              }
              onValueChange={(value) => {
                updateField(FIELDS.TT_PRIVACY_LEVEL, value);
              }}
              disabled={allDisabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a post privacy level">
                  {renderPrivacyLevel({
                    value:
                      get(postFormValue, FIELDS.TT_PRIVACY_LEVEL) ||
                      FIELDS_DEFAULT.TT_PRIVACY_LEVEL,
                    label:
                      postPrivacyConfig.find(
                        (c) =>
                          c.value ===
                          (get(postFormValue, FIELDS.TT_PRIVACY_LEVEL) ||
                            FIELDS_DEFAULT.TT_PRIVACY_LEVEL)
                      )?.label || "",
                  })}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {postPrivacyConfig.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {renderPrivacyLevel(option)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {renderSwitchField(allowCommentField)}
          {renderSwitchField(autoAddMusicField)}
        </>
      )}
    </TiktokSwitchAllowsWrapper>
  );
};

const renderPrivacyLevel = (data: { value: string; label: string }) => {
  return (
    <PrivacyLevelDisplay
      value={data.value as TiktokPrivacyLevel}
      label={data.label}
    />
  );
};

const PrivacyLevelDisplay = ({
  value,
  label,
}: {
  value: TiktokPrivacyLevel;
  label: string;
}) => {
  return (
    <ItemDisplayContainer>
      {resolvePrivacyLevelIcon(value)}
      <PrivacyLabel>{label}</PrivacyLabel>
    </ItemDisplayContainer>
  );
};

const PrivacyLabel = styled.div`
  margin-left: 8px;
`;

const resolvePrivacyLevelIcon = (value: TiktokPrivacyLevel) => {
  switch (value) {
    case "PUBLIC_TO_EVERYONE":
      return <Icon name="globe-04" size="md" />;
    case "MUTUAL_FOLLOW_FRIENDS":
      return <Icon name="switch-horizontal-01" size="md" />;
    case "FOLLOWER_OF_CREATOR":
      return <Icon name="user-check-01" size="md" />;
    case "SELF_ONLY":
      return <Icon name="lock-01" size="md" />;
    default:
      return null;
  }
};

const renderCoverIndex = (mediaFiles: MediaUrlInfo[]) => {
  // eslint-disable-next-line react/display-name
  return (data: { value: number }) => {
    const media = mediaFiles[data.value];
    return (
      <CoverIndexDisplay
        index={data.value}
        src={media.thumbnailUrl || media.url}
      />
    );
  };
};
const CoverIndexDisplay = ({ index, src }: { index: number; src: string }) => {
  return (
    <ItemDisplayContainer>
      <NumberIcon value={index} />
      <CoverPreivewImg src={src} />
    </ItemDisplayContainer>
  );
};

const NumberIcon = ({ value }: { value: number }) => {
  return <NumberIconBox>{value}</NumberIconBox>;
};

const ItemDisplayContainer = styled.div`
  display: flex;
  align-items: center;
`;
const CoverPreivewImg = styled.img`
  width: 24px;
  height: 24px;
`;

const NumberIconBox = styled.div`
  width: 20px;
  height: 20px;
  border: 1px solid #4f31ff;
  border-radius: 4px;
  color: #4f31ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
`;

const LabelStyled = styled(PostFieldLabel)`
  margin-bottom: 1rem;
  display: flex;
  white-space: pre;
`;

const TiktokSwitchAllowsWrapper = styled.div`
  margin-bottom: 0;
`;
